import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { flexRender, functionalUpdate, useTable } from "@tanstack/react-table";
import { RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Button } from "@/src/components/ui/button";
import { Skeleton } from "@/src/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/src/components/ui/table";
import { cn } from "@/src/lib/utils";

import DataTableBulkActions from "./DataTableBulkActions";
import { DATA_TABLE_FEATURES } from "./dataTableFeatures";
import { matchesFilterFn, searchFilterFn } from "./dataTableFilters";
import DataTablePagination from "./DataTablePagination";
import DataTableSortableRow from "./DataTableSortableRow";
import {
  BODY_CELL_CLASS,
  EMPTY_CELL_CLASS,
  getColumnAlignClass,
  getTableMinWidth,
  getVisibleColumnLayouts,
  PAGE_TITLE_CLASS,
} from "./dataTableStyles";
import DataTableToolbar from "./DataTableToolbar";
import useDataTableState from "../../hooks/useDataTableState";
import useStoredColumnVisibility from "../../hooks/useStoredColumnVisibility";
import { hasResetValueChanged } from "../../utils/hasResetValueChanged";

import type { Announcements, DragEndEvent } from "@dnd-kit/core";
import type { RowData, RowSelectionState } from "@tanstack/react-table";
import type { AriaAttributes, JSX, MouseEvent, ReactNode } from "react";

import type {
  BulkAction,
  DataTableCell,
  DataTableColumn,
  DataTableColumnDef,
  DataTableRow,
  DataTableServerState,
  DataTableSorting,
  ResetPageValue,
  UrlSyncOption,
} from "./dataTable.types";

// Avec border-collapse, la hauteur inclut la bordure : 48 px laisse de la marge au bouton « … ».
const NO_RESET_VALUE_LIST: ResetPageValue[] = [];

const BODY_ROW_CLASS = "h-12";
// Le survol de TableRow n'a de sens que sur les lignes de données.
const STATIC_ROW_CLASS = "hover:bg-transparent";
const INTERACTIVE_SELECTOR = "a, button, input, [role='menuitem']";
// Souris : distance avant de saisir, pour ne pas confondre avec un clic ; tactile : appui court, pour ne pas bloquer le défilement.
const MOUSE_ACTIVATION = { distance: 5 };
const TOUCH_ACTIVATION = { delay: 200, tolerance: 8 };
const DRAG_MODIFIER_LIST = [restrictToVerticalAxis];

interface DataTableBaseProps<TData extends RowData> {
  columns: DataTableColumnDef<TData>[];
  data: TData[];
  /** Les lignes affichées ne correspondent pas (encore) à l'état demandé (pas pour un rafraîchissement en arrière-plan). */
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => unknown;
  title?: string;
  description?: ReactNode;
  headerActions?: ReactNode;
  emptyText?: string;
  emptyState?: ReactNode;
  /** `undefined` pour une ligne sans page : inerte, les autres gardent leur lien. */
  getRowHref?: (row: TData) => string | undefined;
  onRowClick?: (row: TData) => void;
  getRowId?: (row: TData) => string;
  /** Clé unique dans l'app sous laquelle le navigateur retient les colonnes masquées. */
  storageKey?: string;
}

interface DataTableClientProps<
  TData extends RowData,
> extends DataTableBaseProps<TData> {
  serverState?: never;
  rowCount?: never;
  urlSync?: UrlSyncOption;
  defaultSorting?: DataTableSorting<TData>;
  /** 10 par défaut, 100 au plus. */
  defaultPageSize?: number;
  /** Valeurs externes au tableau (ex. une période) : quand l'une change, retour à la page 1. */
  resetPageOn?: ResetPageValue[];
  compactPagination?: boolean;
  /** Reçoit la liste complète réordonnée à chaque dépôt ; ni pagination, ni tri, ni recherche, ni filtre, ni URL. */
  onReorder?: (rows: TData[]) => void;
  bulkActions?: BulkAction<TData>[];
}

interface DataTableServerProps<
  TData extends RowData,
> extends DataTableBaseProps<TData> {
  serverState: DataTableServerState;
  rowCount: number;
  urlSync?: never;
  defaultSorting?: never;
  defaultPageSize?: never;
  resetPageOn?: never;
  compactPagination?: never;
  onReorder?: never;
  bulkActions?: never;
}

type DataTableProps<TData extends RowData> =
  | DataTableClientProps<TData>
  | DataTableServerProps<TData>;

function getDefaultRowId<TData extends RowData>(
  row: TData,
  index: number,
): string {
  if (
    typeof row === "object" &&
    row !== null &&
    "uuid" in row &&
    typeof row.uuid === "string"
  ) {
    return row.uuid;
  }
  return String(index);
}

// Le `meta` de `getColumnCanGlobalFilter` n'a pas notre type : lu sans cast.
function isSearchableMeta(meta: unknown): boolean {
  return (
    typeof meta === "object" &&
    meta !== null &&
    "searchable" in meta &&
    meta.searchable === true
  );
}

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

// Valeurs vides à la fin : l'ordre doit rester total, sinon le tri est faux.
function naturalSortFn<TData extends RowData>(
  rowA: DataTableRow<TData>,
  rowB: DataTableRow<TData>,
  columnId: string,
): number {
  const valueA = rowA.getValue(columnId);
  const valueB = rowB.getValue(columnId);

  if (isEmptyValue(valueA) || isEmptyValue(valueB)) {
    return Number(isEmptyValue(valueA)) - Number(isEmptyValue(valueB));
  }
  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueA - valueB;
  }
  return String(valueA).localeCompare(String(valueB), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

const DEFAULT_COLUMN = {
  sortFn: naturalSortFn,
  filterFn: matchesFilterFn,
  enableHiding: false,
};

// Appelé sans flexRender pour lire le texte rendu : un rendu de cellule ne peut donc pas utiliser de hook.
function renderCellContent<TData extends RowData>(
  cell: DataTableCell<TData>,
): ReactNode {
  const template = cell.column.columnDef.cell;
  if (typeof template !== "function") return template;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TanStack type le rendu d'une cellule en `any`.
  return template(cell.getContext());
}

function getAriaSort<TData extends RowData>(
  column: DataTableColumn<TData>,
): AriaAttributes["aria-sort"] {
  if (!column.getCanSort()) return undefined;

  const sortDirection = column.getIsSorted();
  if (sortDirection === "asc") return "ascending";
  if (sortDirection === "desc") return "descending";
  return "none";
}

function DataTable<TData extends RowData>(
  props: DataTableProps<TData>,
): JSX.Element {
  const {
    columns,
    data,
    isLoading: isLoadingProp = false,
    isError = false,
    onRetry,
    title,
    description,
    headerActions,
    emptyText,
    emptyState,
    getRowHref,
    onRowClick,
    getRowId,
    defaultSorting,
    urlSync,
    defaultPageSize,
    resetPageOn = NO_RESET_VALUE_LIST,
    compactPagination = false,
    onReorder,
    bulkActions,
    serverState,
    rowCount,
    storageKey,
  } = props;
  const { t } = useTranslation("table");
  const history = useHistory();
  const isServer = serverState !== undefined;
  const isReorderable = onReorder !== undefined;
  const isManual = isServer || isReorderable;
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: MOUSE_ACTIVATION }),
    useSensor(TouchSensor, { activationConstraint: TOUCH_ACTIVATION }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const { columnVisibility, onColumnVisibilityChange } =
    useStoredColumnVisibility(storageKey);

  // Toujours appelé (règles des hooks) ; inerte en mode serveur.
  const clientState = useDataTableState({
    columns,
    defaultSorting,
    urlSync: isManual ? false : urlSync,
    defaultPageSize,
  });

  const controls = serverState?.tableState ?? clientState;
  const { state } = controls;

  const columnFilterList = useMemo(
    () => Object.entries(state.filters).map(([id, value]) => ({ id, value })),
    [state.filters],
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isRetrying, setIsRetrying] = useState(false);
  const isLoading = isLoadingProp || isRetrying;

  const pagination = { pageIndex: state.pageIndex, pageSize: state.pageSize };

  const table = useTable({
    features: DATA_TABLE_FEATURES,
    data,
    columns,
    getRowId: getRowId ?? getDefaultRowId,
    state: {
      pagination,
      sorting: state.sorting,
      globalFilter: state.search,
      columnFilters: columnFilterList,
      columnVisibility,
      rowSelection,
    },
    onColumnVisibilityChange,
    onRowSelectionChange: (updater) =>
      setRowSelection(functionalUpdate(updater, rowSelection)),
    onPaginationChange: (updater) => {
      const nextPagination = functionalUpdate(updater, pagination);
      controls.setPagination(nextPagination.pageIndex, nextPagination.pageSize);
    },
    onSortingChange: (updater) =>
      controls.setSorting(functionalUpdate(updater, state.sorting)),
    globalFilterFn: searchFilterFn,
    getColumnCanGlobalFilter: (column) =>
      isSearchableMeta(column.columnDef.meta),
    defaultColumn: DEFAULT_COLUMN,
    manualPagination: isManual,
    manualSorting: isManual,
    manualFiltering: isManual,
    rowCount,
    autoResetPageIndex: false,
  });

  // Oublie le masquage d'une colonne devenue non masquable (gardé pour une colonne absente : rôle en chargement).
  useEffect(() => {
    const lockedColumnIdList = table
      .getAllLeafColumns()
      .filter((column) => !column.getCanHide())
      .map((column) => column.id);
    const hasStaleHiddenColumns = Object.keys(columnVisibility).some((id) =>
      lockedColumnIdList.includes(id),
    );
    if (!hasStaleHiddenColumns) return;

    onColumnVisibilityChange((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(
          ([id]) => !lockedColumnIdList.includes(id),
        ),
      ),
    );
  }, [table, columns, columnVisibility, onColumnVisibilityChange]);

  const { rowsById } = table.getCoreRowModel();
  const selectedIdList = Object.keys(rowSelection);
  const keptIdList = selectedIdList.filter((id) => rowsById[id] !== undefined);

  if (keptIdList.length !== selectedIdList.length) {
    setRowSelection(Object.fromEntries(keptIdList.map((id) => [id, true])));
  }

  const { pageIndex, pageSize } = state;
  const pageCount = table.getPageCount();
  const { setPagination } = controls;

  const previousResetValues = useRef(resetPageOn);

  useEffect(() => {
    if (!hasResetValueChanged(previousResetValues.current, resetPageOn)) return;

    previousResetValues.current = resetPageOn;
    if (pageIndex > 0) setPagination(0, pageSize);
  }, [resetPageOn, pageIndex, pageSize, setPagination]);

  // Pas pendant un chargement (`rowCount` d'avant) ni après une erreur (un total à 0 ne prouve rien).
  useEffect(() => {
    if (isLoading || isError || pageIndex === 0 || pageIndex < pageCount) {
      return;
    }

    setPagination(Math.max(0, pageCount - 1), pageSize);
  }, [isLoading, isError, pageIndex, pageSize, pageCount, setPagination]);

  const { rows: rowList } = table.getRowModel();
  const getSelectedRowList = (): TData[] => {
    const visibleRowList = table.getPrePaginatedRowModel().rows;
    const visibleIdSet = new Set(visibleRowList.map((row) => row.id));

    return [
      ...visibleRowList.filter((row) => row.getIsSelected()),
      ...table
        .getCoreRowModel()
        .rows.filter((row) => row.getIsSelected() && !visibleIdSet.has(row.id)),
    ].map((row) => row.original);
  };
  const selectedRowList =
    bulkActions && !isReorderable && selectedIdList.length > 0
      ? getSelectedRowList()
      : [];
  const hasActiveQuery =
    state.search !== "" ||
    Object.keys(state.filters).length > 0 ||
    state.pageIndex > 0;
  const hasNoData = rowCount === undefined ? data.length === 0 : rowCount === 0;
  const showsError = isError && !isLoading && data.length === 0;

  if (emptyState && !isLoading && !showsError && hasNoData && !hasActiveQuery) {
    return <React.Fragment>{emptyState}</React.Fragment>;
  }

  const isTableInteractive = Boolean(getRowHref ?? onRowClick);
  const isRowInteractive = (row: TData): boolean =>
    Boolean(onRowClick) || getRowHref?.(row) !== undefined;

  const activateRow = (row: TData, inNewTab: boolean): void => {
    const href = getRowHref?.(row);

    if (href === undefined) {
      onRowClick?.(row);
    } else if (inNewTab) {
      window.open(href, "_blank", "noopener");
    } else {
      history.push(href);
    }
  };

  const isFromInteractiveElement = (target: EventTarget): boolean =>
    target instanceof Element && target.closest(INTERACTIVE_SELECTOR) !== null;

  const handleRowClick = (
    event: MouseEvent<HTMLTableRowElement>,
    row: TData,
  ): void => {
    if (isFromInteractiveElement(event.target)) return;

    activateRow(row, event.metaKey || event.ctrlKey);
  };

  const handleRowAuxClick = (
    event: MouseEvent<HTMLTableRowElement>,
    row: TData,
  ): void => {
    if (event.button !== 1 || getRowHref?.(row) === undefined) return;
    if (isFromInteractiveElement(event.target)) return;

    activateRow(row, true);
  };

  const visibleColumnList = table.getVisibleLeafColumns();
  const visibleLayoutList = getVisibleColumnLayouts(
    table.getAllLeafColumns().map((column) => ({
      id: column.id,
      meta: column.columnDef.meta,
      isVisible: column.getIsVisible(),
    })),
  );
  const tableMinWidth = getTableMinWidth(visibleLayoutList);
  const widthByColumnId = new Map(
    visibleLayoutList.map(({ id, meta }) => [id, meta?.width]),
  );

  const rowIdList = rowList.map((row) => row.id);

  const getPosition = (id: string | number): number =>
    rowIdList.indexOf(String(id)) + 1;

  const announcements: Announcements = {
    onDragStart: ({ active }) =>
      t("dataTable.dragStart", {
        position: getPosition(active.id),
        total: rowIdList.length,
      }),
    onDragOver: ({ over }) =>
      over
        ? t("dataTable.dragOver", {
            position: getPosition(over.id),
            total: rowIdList.length,
          })
        : undefined,
    onDragEnd: ({ over, active }) =>
      t("dataTable.dragEnd", {
        position: getPosition(over?.id ?? active.id),
        total: rowIdList.length,
      }),
    onDragCancel: ({ active }) =>
      t("dataTable.dragCancel", {
        position: getPosition(active.id),
        total: rowIdList.length,
      }),
  };

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (!over || active.id === over.id) return;

    const oldIndex = rowIdList.indexOf(String(active.id));
    const newIndex = rowIdList.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder?.(
      arrayMove(
        rowList.map((row) => row.original),
        oldIndex,
        newIndex,
      ),
    );
  };

  // React Query v4 garde `isError` pendant une relance : le squelette vient de l'appel lui-même.
  const handleRetry = (): void => {
    setIsRetrying(true);
    Promise.resolve(onRetry?.()).finally(() => setIsRetrying(false));
  };

  const renderBody = (): ReactNode => {
    // Une ligne de squelette par ligne de page : la hauteur ne saute pas.
    if (isLoading) {
      return Array.from({ length: pageSize }, (_, rowIndex) => (
        <TableRow
          key={rowIndex}
          className={cn(BODY_ROW_CLASS, STATIC_ROW_CLASS)}
          data-testid="DataTable-skeleton-row"
        >
          {visibleColumnList.map((column) => (
            <TableCell key={column.id}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (showsError) {
      return (
        <TableRow className={STATIC_ROW_CLASS}>
          <TableCell
            className="h-24 text-center"
            colSpan={visibleColumnList.length}
          >
            <div className="flex flex-col items-center gap-2" role="alert">
              <span className="text-muted-foreground">
                {t("dataTable.loadError")}
              </span>
              {onRetry ? (
                <Button onClick={handleRetry} size="sm" variant="outline">
                  <RotateCw />
                  {t("dataTable.retry")}
                </Button>
              ) : null}
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (rowList.length === 0) {
      return (
        <TableRow className={STATIC_ROW_CLASS}>
          <TableCell
            className="text-muted-foreground h-24 text-center"
            colSpan={visibleColumnList.length}
          >
            {emptyText ?? t("common:no_results")}
          </TableCell>
        </TableRow>
      );
    }

    return rowList.map((row) => {
      const interactive = isRowInteractive(row.original);
      const rowClassName = cn(
        BODY_ROW_CLASS,
        interactive && "cursor-pointer",
        isTableInteractive && !interactive && STATIC_ROW_CLASS,
      );
      const handleAuxClick = interactive
        ? (event: MouseEvent<HTMLTableRowElement>): void =>
            handleRowAuxClick(event, row.original)
        : undefined;
      const handleClick = interactive
        ? (event: MouseEvent<HTMLTableRowElement>): void =>
            handleRowClick(event, row.original)
        : undefined;
      const cellList = row.getVisibleCells().map((cell) => {
        const content = renderCellContent(cell);

        return (
          <TableCell
            key={cell.id}
            title={typeof content === "string" ? content : undefined}
            className={cn(
              BODY_CELL_CLASS,
              cell.column.id !== "actions" && EMPTY_CELL_CLASS,
              getColumnAlignClass(cell.column),
            )}
          >
            {content}
          </TableCell>
        );
      });

      return isReorderable ? (
        <DataTableSortableRow
          key={row.id}
          className={rowClassName}
          onAuxClick={handleAuxClick}
          onClick={handleClick}
          rowId={row.id}
        >
          {cellList}
        </DataTableSortableRow>
      ) : (
        <TableRow
          key={row.id}
          className={rowClassName}
          onAuxClick={handleAuxClick}
          onClick={handleClick}
        >
          {cellList}
        </TableRow>
      );
    });
  };

  const tableBody = <TableBody aria-busy={isLoading}>{renderBody()}</TableBody>;

  // Hors du `<table>` : DndContext rend une consigne cachée pour les lecteurs d'écran.
  const withReorderContext = (children: ReactNode): ReactNode =>
    isReorderable ? (
      <DndContext
        collisionDetection={rectIntersection}
        modifiers={DRAG_MODIFIER_LIST}
        onDragEnd={handleDragEnd}
        sensors={sensors}
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable: t("dataTable.dragInstructions"),
          },
        }}
      >
        {children}
      </DndContext>
    ) : (
      children
    );

  return (
    <div className="flex flex-col gap-4" data-testid="DataTable">
      {title || description || headerActions ? (
        <div
          className="flex flex-wrap items-start justify-between gap-4"
          data-testid="DataTableHeader"
        >
          <div className="flex flex-col gap-1">
            {title ? <h2 className={PAGE_TITLE_CLASS}>{title}</h2> : null}
            {description}
          </div>
          {headerActions ? (
            <div className="ml-auto flex items-center gap-2">
              {headerActions}
            </div>
          ) : null}
        </div>
      ) : null}
      <DataTableToolbar
        filters={state.filters}
        onFilterChange={controls.setFilter}
        onReset={controls.resetFilters}
        onSearchChange={controls.setSearch}
        search={state.search}
        table={table}
        bulkActions={
          bulkActions && !isReorderable && selectedRowList.length > 0 ? (
            <DataTableBulkActions
              actions={bulkActions}
              disabled={isLoading}
              onClearSelection={() => table.resetRowSelection(true)}
              rows={selectedRowList}
            />
          ) : null
        }
      />
      {/* overflow-hidden : le fond de l'en-tête suit les coins arrondis. */}
      {withReorderContext(
        <div className="bg-card overflow-hidden rounded-md border">
          <Table className="table-fixed" style={{ minWidth: tableMinWidth }}>
            <TableHeader className="dark:bg-secondary">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className={STATIC_ROW_CLASS}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      aria-sort={getAriaSort(header.column)}
                      className={getColumnAlignClass(header.column)}
                      style={{ width: widthByColumnId.get(header.column.id) }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            {isReorderable ? (
              <SortableContext
                items={rowIdList}
                strategy={verticalListSortingStrategy}
              >
                {tableBody}
              </SortableContext>
            ) : (
              tableBody
            )}
          </Table>
        </div>,
      )}
      {isReorderable ? null : (
        <DataTablePagination compact={compactPagination} table={table} />
      )}
    </div>
  );
}

export default DataTable;
